import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, ShieldCheck, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { NAV_CATEGORIES, CATEGORY_META } from "@/i18n/translations";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = {
  category: "opportunities", intent: "offer",
  title_fr: "", title_en: "", summary_fr: "", summary_en: "",
  description_fr: "", description_en: "", sector: "", country: "", region: "",
  tags: [], cover_image: "", status: "draft", featured: false, order: 0,
  verification: { level: "unverified", source_fr: "", source_en: "", last_verified_at: null, history: [] },
};

const inputCls = "w-full border border-tertiary bg-white px-3 py-2 text-sm outline-none focus:border-champagne focus:ring-1 focus:ring-champagne";
const labelCls = "mb-1 block text-xs uppercase tracking-[0.12em] text-anthracite/50";

export default function ListingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyForm, setVerifyForm] = useState({ level: "verified", source_fr: "", source_en: "", note_fr: "", note_en: "" });

  useEffect(() => {
    if (editing) {
      api.get(`/admin/listings/${id}`).then(({ data }) => {
        setForm({ ...EMPTY, ...data, verification: { ...EMPTY.verification, ...(data.verification || {}) } });
        setTagsInput((data.tags || []).join(", "));
      }).catch(() => toast.error("Introuvable"));
    }
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runAI = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/structure", { raw_text: aiText, category: form.category });
      const d = data.data || {};
      setForm((f) => ({
        ...f,
        title_fr: d.title_fr || f.title_fr, title_en: d.title_en || f.title_en,
        summary_fr: d.summary_fr || f.summary_fr, summary_en: d.summary_en || f.summary_en,
        description_fr: d.description_fr || f.description_fr, description_en: d.description_en || f.description_en,
        sector: d.sector || f.sector, country: d.country || f.country, region: d.region || f.region,
        intent: d.intent || f.intent,
      }));
      if (Array.isArray(d.tags)) setTagsInput(d.tags.join(", "));
      toast.success("Fiche structurée par l'IA — vérifiez et ajustez.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec IA");
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing) {
        await api.put(`/admin/listings/${id}`, payload);
      } else {
        await api.post("/admin/listings", payload);
      }
      toast.success("Enregistré");
      navigate("/admin/listings");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const submitVerify = async () => {
    try {
      const { data } = await api.post(`/admin/listings/${id}/verify`, verifyForm);
      setForm((f) => ({ ...f, verification: data.verification }));
      setVerifyOpen(false);
      toast.success("Vérification enregistrée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/admin/listings")} className="mb-6 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne">
        <ArrowLeft className="h-4 w-4" /> Retour aux fiches
      </button>
      <h1 className="mb-8 font-serif text-3xl text-anthracite">{editing ? "Modifier la fiche" : "Nouvelle fiche"}</h1>

      {/* AI ASSISTANT */}
      <div className="mb-8 border border-champagne/30 bg-champagne/5 p-5" data-testid="ai-assistant">
        <div className="mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-champagne" />
          <h2 className="font-medium text-anthracite">Assistant IA — structurer une opportunité</h2>
        </div>
        <textarea
          data-testid="ai-input" value={aiText} onChange={(e) => setAiText(e.target.value)} rows={3}
          placeholder="Collez ici un texte brut décrivant l'opportunité, le projet ou le besoin…"
          className={inputCls}
        />
        <button onClick={runAI} disabled={aiLoading} data-testid="ai-run" className="mt-3 inline-flex items-center gap-2 bg-champagne px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-champagne-hover disabled:opacity-50">
          <Sparkles className="h-4 w-4" /> {aiLoading ? "Structuration…" : "Structurer avec l'IA"}
        </button>
      </div>

      <div className="space-y-6 border border-tertiary bg-white p-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Domaine</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="field-category" className={inputCls}>
              {NAV_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_META[c].fr}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Intention</label>
            <select value={form.intent} onChange={(e) => set("intent", e.target.value)} className={inputCls}>
              <option value="offer">Propose</option>
              <option value="seek">Recherche</option>
              <option value="both">Les deux</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Statut</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="field-status" className={inputCls}>
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
              <option value="archived">Archivé</option>
            </select>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Titre (FR)</label>
            <input value={form.title_fr} onChange={(e) => set("title_fr", e.target.value)} data-testid="field-title-fr" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Titre (EN)</label>
            <input value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Résumé (FR)</label>
            <textarea rows={2} value={form.summary_fr} onChange={(e) => set("summary_fr", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Résumé (EN)</label>
            <textarea rows={2} value={form.summary_en} onChange={(e) => set("summary_en", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description (FR)</label>
            <textarea rows={5} value={form.description_fr} onChange={(e) => set("description_fr", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description (EN)</label>
            <textarea rows={5} value={form.description_en} onChange={(e) => set("description_en", e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div><label className={labelCls}>Secteur</label><input value={form.sector} onChange={(e) => set("sector", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Pays</label><input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Région</label><input value={form.region} onChange={(e) => set("region", e.target.value)} className={inputCls} /></div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={labelCls}>Tags (séparés par des virgules)</label><input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className={inputCls} /></div>
          <ImageUpload value={form.cover_image} onChange={(v) => set("cover_image", v)} label="Image de couverture" testid="listing-cover" />
        </div>

        <label className="flex items-center gap-2 text-sm text-anthracite/70">
          <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="field-featured" className="h-4 w-4 accent-champagne" />
          Mettre en avant sur la page d'accueil
        </label>

        {/* Verification */}
        <div className="border-t border-tertiary pt-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-anthracite">Vérification RSTI</p>
              <p className="text-xs text-anthracite/50">Niveau actuel : <span className="text-champagne">{form.verification?.level}</span></p>
            </div>
            {editing && (
              <button onClick={() => setVerifyOpen((o) => !o)} data-testid="verify-toggle" className="inline-flex items-center gap-2 border border-champagne px-4 py-2 text-sm text-champagne transition-colors hover:bg-champagne hover:text-white">
                <ShieldCheck className="h-4 w-4" /> Vérifier
              </button>
            )}
          </div>
          {verifyOpen && (
            <div className="mt-4 grid gap-3 border border-champagne/20 bg-champagne/5 p-4 sm:grid-cols-2" data-testid="verify-panel">
              <div>
                <label className={labelCls}>Niveau</label>
                <select value={verifyForm.level} onChange={(e) => setVerifyForm((v) => ({ ...v, level: e.target.value }))} className={inputCls}>
                  <option value="declared">Déclaré</option>
                  <option value="verified">Vérifié</option>
                  <option value="premium">Premium vérifié</option>
                  <option value="unverified">Non vérifié</option>
                </select>
              </div>
              <div><label className={labelCls}>Source (FR)</label><input value={verifyForm.source_fr} onChange={(e) => setVerifyForm((v) => ({ ...v, source_fr: e.target.value }))} className={inputCls} /></div>
              <div><label className={labelCls}>Note (FR)</label><input value={verifyForm.note_fr} onChange={(e) => setVerifyForm((v) => ({ ...v, note_fr: e.target.value }))} className={inputCls} /></div>
              <div><label className={labelCls}>Note (EN)</label><input value={verifyForm.note_en} onChange={(e) => setVerifyForm((v) => ({ ...v, note_en: e.target.value }))} className={inputCls} /></div>
              <div className="sm:col-span-2">
                <button onClick={submitVerify} data-testid="verify-submit" className="bg-anthracite px-4 py-2 text-sm text-ivory hover:bg-black">Enregistrer la vérification</button>
              </div>
            </div>
          )}
          {form.verification?.history?.length > 0 && (
            <ul className="mt-4 space-y-1 text-xs text-anthracite/50">
              {form.verification.history.slice().reverse().map((h, i) => (
                <li key={i}>{new Date(h.at).toLocaleDateString("fr")} — {h.level} — {h.by}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button onClick={() => navigate("/admin/listings")} className="border border-tertiary px-6 py-3 text-sm text-anthracite/70 hover:bg-sand">Annuler</button>
        <button onClick={save} disabled={saving} data-testid="save-listing" className="inline-flex items-center gap-2 bg-anthracite px-6 py-3 text-sm font-medium text-ivory hover:bg-black disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}
